/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { MyShowsUserSettings, MyShowsUserSettingsValue } from 'client-code/user-settings';

import { NdvrInteractor } from '../../../../core/interactors';
import { Locale, LocaleKeys } from '../../../../core/locale';
import type { TivoPayloadAction } from '../../../../core/tivo/tivo-redux-wrapper';
import { WhisperTypes } from '../../../../views/Whisper/WhisperTypes';
import { showMyShowsOptionsMenu } from '../../../screens/screens.reducers';
import { showWhisper } from '../../../whisper/whisper.reducer';
import { setOnePassAndRecordingOrMyShowsGetErrorAction } from '../userpreferences.actions';
import {
    setMyShowsUserSettingsErrorAction,
    setMyShowsUserSettingsOptionsAction,
    setMyShowsUserSettingsValueAction,
} from './myshowsoptionsmenu.actions';
import {
    SET_MY_SHOWS_USER_SETTINGS_ERROR,
    SET_MY_SHOWS_USER_SETTINGS_FOCUSED_OPTION_INDEX,
    SET_MY_SHOWS_USER_SETTINGS_OPTIONS,
    SET_MY_SHOWS_USER_SETTINGS_VALUE,
} from './myshowsoptionsmenu.types';

const initialState: MyShowsUserSettings & {
    focusedOptionIndex: number;
    hasError: boolean;
    isFocused: boolean;
} = {
    settingsOptions: undefined,
    settingsValues: undefined,
    focusedOptionIndex: 0,
    hasError: false,
    isFocused: true,
};

const myShowsUserSettingsReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<Partial<typeof initialState>>>{},
) => {
    switch (type) {
        case SET_MY_SHOWS_USER_SETTINGS_OPTIONS:
            return {
                ...state,
                settingsOptions: payload.settingsOptions,
            };
        case SET_MY_SHOWS_USER_SETTINGS_VALUE:
            return {
                ...state,
                settingsValues: payload.settingsValues,
            };
        case SET_MY_SHOWS_USER_SETTINGS_FOCUSED_OPTION_INDEX:
            return {
                ...state,
                focusedOptionIndex: payload.focusedOptionIndex,
                isFocused: payload.isFocused,
            };

        case SET_MY_SHOWS_USER_SETTINGS_ERROR:
            return {
                ...state,
                hasError: payload.hasError,
            };
        default:
            return state;
    }
};

export const openMyShowsOptionsMenu = () => async (dispatch: Function) => {
    dispatch(showMyShowsOptionsMenu());
    dispatch(getMyShowsUserSettings());
};

const getMyShowsUserSettings = () => async (dispatch: Function) => {
    const ndvrInteractor = new NdvrInteractor();
    let response: MyShowsUserSettings;

    try {
        response = await ndvrInteractor.getMyShowsUserSettings();
        dispatch(setOnePassAndRecordingOrMyShowsGetErrorAction(false));
    } catch (error) {
        dispatch(setOnePassAndRecordingOrMyShowsGetErrorAction(true));
        return dispatch(
            showWhisper({
                message: Locale.getString(LocaleKeys.SOMETHING_WENT_WRONG_WHISPER_DESCRIPTION),
                iconType: WhisperTypes.INFO,
            }),
        );
    }

    dispatch(setMyShowsUserSettingsValueAction(response.settingsValues ?? {}));
    dispatch(
        setMyShowsUserSettingsOptionsAction(response.settingsOptions ?? { myShowsOrders: [] }),
    );
};

export const setMyShowsUserSettings =
    (selectedOption: MyShowsUserSettingsValue, hasError: boolean) => async (dispatch: Function) => {
        const ndvrInteractor = new NdvrInteractor();

        if (!selectedOption) return;

        dispatch(setMyShowsUserSettingsValueAction(selectedOption));
        try {
            await ndvrInteractor.setMyShowsUserSettings(selectedOption);
            if (hasError) {
                dispatch(setMyShowsUserSettingsErrorAction(false));
            }
        } catch (error) {
            dispatch(setMyShowsUserSettingsErrorAction(true));
        }
    };

export default myShowsUserSettingsReducer;
